import React, { useState, useEffect, useRef } from 'react';
import { Term, QuizState } from './types';
import { QuizService } from './QuizService';
import QuizQuestion from './QuizQuestion';
import QuizScore from './QuizScore';
import QuizComplete from './QuizComplete';
import QuizTimer from './QuizTimer';
import './App.css';

function App() {
  const [terms, setTerms] = useState<Term[]>([]);
  const [quizState, setQuizState] = useState<QuizState>({
    questions: [],
    currentQuestionIndex: 0,
    score: 0,
    totalQuestions: 0,
    isQuizComplete: false,
    selectedAnswer: null,
    showResult: false,
    isCorrect: null,
    timeLeft: 30,
    timerActive: false,
    timeUp: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadTerms();
  }, []);

  // Timer effect
  useEffect(() => {
    if (quizState.timerActive && quizState.timeLeft > 0 && !quizState.showResult) {
      timerRef.current = setTimeout(() => {
        setQuizState(prev => ({
          ...prev,
          timeLeft: prev.timeLeft - 1
        }));
      }, 1000);
    } else if (quizState.timeLeft === 0 && !quizState.showResult) {
      // Time's up - auto-advance to next question
      handleTimeUp();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [quizState.timeLeft, quizState.timerActive, quizState.showResult]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const loadTerms = async () => {
    try {
      const response = await fetch('/terms.json');
      const data = await response.json();
      // Extract the firefighting_terms array from the nested structure
      const termsData: Term[] = data.firefighting_terms || [];
      setTerms(termsData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading terms:', error);
      setIsLoading(false);
    }
  };

  const startQuiz = () => {
    if (terms.length === 0) return;
    
    const quizService = new QuizService(terms);
    const questions = quizService.generateQuiz();
    
    setQuizState({
      questions,
      currentQuestionIndex: 0,
      score: 0,
      totalQuestions: questions.length,
      isQuizComplete: false,
      selectedAnswer: null,
      showResult: false,
      isCorrect: null,
      timeLeft: 30,
      timerActive: true,
      timeUp: false
    });
  };

  const handleTimeUp = () => {
    // Time's up - mark as incorrect and show result
    setQuizState(prev => ({
      ...prev,
      selectedAnswer: null,
      showResult: true,
      isCorrect: false,
      timerActive: false,
      timeUp: true
    }));
  };

  const handleAnswerSelect = (answerIndex: number) => {
    if (quizState.showResult) return;
    
    const isCorrect = answerIndex === quizState.questions[quizState.currentQuestionIndex].correctAnswerIndex;
    
    if (isCorrect) {
      // Correct answer - move to next question immediately
      const newScore = quizState.score + 1;
      const nextQuestionIndex = quizState.currentQuestionIndex + 1;
      
      if (nextQuestionIndex >= quizState.totalQuestions) {
        setQuizState(prev => ({
          ...prev,
          score: newScore,
          isQuizComplete: true,
          timerActive: false
        }));
      } else {
        setQuizState(prev => ({
          ...prev,
          currentQuestionIndex: nextQuestionIndex,
          score: newScore,
          selectedAnswer: null,
          showResult: false,
          isCorrect: null,
          timeLeft: 30,
          timerActive: true,
          timeUp: false
        }));
      }
    } else {
      // Wrong answer - show result
      setQuizState(prev => ({
        ...prev,
        selectedAnswer: answerIndex,
        showResult: true,
        isCorrect: false,
        timerActive: false,
        timeUp: false
      }));
    }
  };

  const handleNextQuestion = () => {
    // This function is now only used for wrong answers
    const nextQuestionIndex = quizState.currentQuestionIndex + 1;
    
    if (nextQuestionIndex >= quizState.totalQuestions) {
      setQuizState(prev => ({
        ...prev,
        isQuizComplete: true,
        timerActive: false
      }));
    } else {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: nextQuestionIndex,
        selectedAnswer: null,
        showResult: false,
        isCorrect: null,
        timeLeft: 30,
        timerActive: true,
        timeUp: false
      }));
    }
  };

  const restartQuiz = () => {
    startQuiz();
  };

  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading-container">
          <div className="loading-spinner">🚒</div>
          <h2>Nalagam gasilski kviz...</h2>
          <p>Pripravljam se na raziskovanje gasilskega znanja!</p>
        </div>
      </div>
    );
  }

  if (quizState.questions.length === 0) {
    return (
      <div className="app-container">
        <div className="welcome-container">
          <h1 className="welcome-title">🚒 Gasilska kviz avantura! 🚒</h1>
          <p className="welcome-subtitle">
            Preizkusi svoje znanje z 20 gasilskimi vprašanji!
          </p>
          <p className="welcome-description">
            Nauči se gasilskih izrazov in varnosti med zabavo. 
            Vsako vprašanje ima 4 možnosti - izberi pravilno!
          </p>
          <button className="start-button" onClick={startQuiz}>
            <span className="button-icon">🚀</span>
            Začni kviz
          </button>
        </div>
      </div>
    );
  }

  if (quizState.isQuizComplete) {
    return (
      <div className="app-container">
        <QuizComplete
          score={quizState.score}
          totalQuestions={quizState.totalQuestions}
          onRestart={restartQuiz}
        />
      </div>
    );
  }

  const currentQuestion = quizState.questions[quizState.currentQuestionIndex];

  return (
    <div className="app-container">
      <QuizScore
        score={quizState.score}
        totalQuestions={quizState.totalQuestions}
        currentQuestion={quizState.currentQuestionIndex + 1}
      />
      
      <QuizQuestion
        question={currentQuestion}
        selectedAnswer={quizState.selectedAnswer}
        showResult={quizState.showResult}
        isCorrect={quizState.isCorrect}
        timeUp={quizState.timeUp}
        onAnswerSelect={handleAnswerSelect}
      />
      
      {quizState.showResult && (
        <div className="next-button-container">
          <button className="next-button" onClick={handleNextQuestion}>
            {quizState.currentQuestionIndex + 1 >= quizState.totalQuestions ? 'Poglej rezultate' : 'Naslednje vprašanje'}
            <span className="button-arrow">→</span>
          </button>
        </div>
      )}
      
      <QuizTimer
        timeLeft={quizState.timeLeft}
        totalTime={30}
        isActive={quizState.timerActive}
      />
    </div>
  );
}

export default App;
